import { forwardRef, useContext } from "react";
import moment from "moment";
import CurrencyFormatter from "../CurrencyFormatter";
import { AppContext } from "../../context/AppContextProvider";
import { AddonName, TransactionOrderSource } from "../../configs/constants";
import { useTranslation } from "react-i18next";

const PrintOrder = forwardRef((props, ref) => {
  const order = props.order;
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const isRtl = appConfig?.direction === "rtl";
  const getLocalizedName = (item) =>
    isRtl
      ? item?.name_ar || item?.name || item?.name_en || ""
      : item?.name_en || item?.name || item?.name_ar || "";
  const orderSourceKeys = {
    [TransactionOrderSource.Pos]: "pos",
    [TransactionOrderSource.QrOrder]: "qr_order",
    [TransactionOrderSource.Kiosk]: "kiosk",
    [TransactionOrderSource.Waiter]: "waiter",
  };

  // Common styles
  const styles = {
    container: { direction: isRtl ? "rtl" : "ltr", padding: "20px" },
    logo: {
      maxHeight: "120px",
      maxWidth: "200px",
      margin: "10px auto 20px",
    },
    divider: {
      borderBottom: "1px solid var(--color-black)",
      paddingTop: "10px",
    },
    dashedDivider: {
      borderBottom: "1px dashed var(--color-black)",
      paddingTop: "10px",
    },
    row: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "start",
      fontSize: "12px",
      paddingTop: "10px",
    },
    receiptHead: {
      borderBottom: "1px solid var(--color-black)",
      paddingTop: "10px",
      paddingBottom: "10px",
      fontWeight: "bold",
    },
    priceColumns: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    unitPrice: {
      width: "70px",
      textAlign: "center",
    },
    subtotalPrice: {
      width: "110px",
      textAlign: "left",
    },
  };

  const renderDiscountSection = () => {
    if (order.discount && !order.reference_transaction) {
      return (
        <>
          <div style={styles.row}>
            <span>{t("subtotal")}</span>
            <span>
              <CurrencyFormatter value={order.sub_total} />
            </span>
          </div>
          <div style={styles.row}>
            <span>
              {t("discount")} % {order.discount.discount_percentage}
            </span>
            <span>
              <CurrencyFormatter value={order.discount_amount} />
            </span>
          </div>
          <div style={styles.divider} />
        </>
      );
    }
  };

  const is_waiter_management_active = authUser.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.WaiterManagement,
  );

  const is_job_management_active = authUser.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.JobManagement,
  );

  const subTotal = Number(order?.sub_total ?? 0);
  const discountAmount = Number(order?.discount_amount ?? 0);
  const netSubTotal = subTotal - discountAmount;

  const items = Array.isArray(order?.items) ? order.items : [];
  const isVatExempt =
    authUser?.is_vat_exempt === 1 || authUser?.is_vat_exempt === true;

  return (
    <div id="slip" ref={ref} style={styles.container}>
      <center>
        {/* logo area */}
        {authUser?.logo ? (
          <img
            id="store-logo"
            style={styles.logo}
            src={authUser.logo}
            alt={t("business_logo")}
          />
        ) : null}
        <br />
        <div>{authUser.business_name}</div>
        <div>{authUser.business_address}</div>
        <div>{authUser.branch_name}</div>
        <div>{isVatExempt ? "" : authUser.vat}</div>
        <div style={styles.divider} />

        {/* Receipt head */}
        <div style={styles.receiptHead}>
          <div style={{ fontWeight: "normal" }}>{t("unpaid_invoice")}</div>
        </div>
      </center>
      {/* Receipt description */}
      <div style={styles.row}>
        <span>{t("order_id")}</span>
        <span>{order.order_id}</span>
      </div>
      {/* -- */}
      {order.order_source &&
        (order.is_drive_thru ? (
          <div style={styles.row}>
            <span>{t("order_source")}</span>
            <span>{t("drive_thru")}</span>
          </div>
        ) : (
          <div style={styles.row}>
            <span>{t("order_source")}</span>
            <span>
              {t(
                orderSourceKeys[Number(order.order_source.value)] ||
                  order.order_source.name,
              )}
            </span>
          </div>
        ))}
      {order.dining_table_name && (
        <div style={styles.row}>
          <span>{t("dining_table")}</span>
          <span>{order.dining_table_name}</span>
        </div>
      )}

      {order.waiter &&
        (is_waiter_management_active || is_job_management_active) && (
          <div style={styles.row}>
            <span>
              {is_waiter_management_active ? t("waiter") : t("job_employee")}
            </span>
            <span>{order.waiter.name}</span>
          </div>
        )}

      {/* -- */}
      {order.buyer_company_name && (
        <div style={styles.row}>
          <span>{t("buyer_company_name")}</span>
          <span>{order.buyer_company_name}</span>
        </div>
      )}
      {order.buyer_company_vat && (
        <div style={styles.row}>
          <span>{t("buyer_company_vat")}</span>
          <span>{order.buyer_company_vat}</span>
        </div>
      )}
      {order.customer_name && (
        <div style={styles.row}>
          <span>{t("customer_name")}</span>
          <span>{order.customer_name}</span>
        </div>
      )}
      {order.vehicle_number && (
        <div style={styles.row}>
          <span>{t("vehicle_number")}</span>
          <span>{order.vehicle_number}</span>
        </div>
      )}
      {order.vehicle_color && (
        <div style={styles.row}>
          <span>{t("vehicle_color")}</span>
          <span>{order.vehicle_color}</span>
        </div>
      )}
      <div style={styles.row}>
        <span>{t("issue_date")}</span>
        <span>{moment(order.created_at).format("MMM. D, YYYY hh:mm a")}</span>
      </div>
      {/* list area */}
      <div style={styles.divider} />
      <div style={styles.row}>
        <span>{t("item")}</span>
        <div style={styles.priceColumns}>
          <span style={styles.unitPrice}>{t("unit_price")}</span>
          <span style={styles.subtotalPrice}>{t("price_including_tax")}</span>
        </div>
      </div>
      <div style={styles.dashedDivider} />
      {items.map((item) => (
        <div style={styles.row} key={"product_items_" + item.id}>
          <span>
            <span dir="ltr">
              {item.quantity} x <bdi dir="auto">{getLocalizedName(item)}</bdi>
            </span>
            {/* <br /> */}
            {/* Show tags if exist */}
            {item.tags && item.tags.length > 0 && (
              <ul
                style={{
                  margin: "2px 0 0 16px",
                  padding: 0,
                  listStyle: "none",
                }}
              >
                {item.tags.map((tag) => (
                  <li key={tag.id} className="app-text-xs">
                    - {getLocalizedName(tag)}
                  </li>
                ))}
              </ul>
            )}
          </span>
          <div style={styles.priceColumns}>
            <span style={styles.unitPrice}>
              <CurrencyFormatter value={item.price} currencyCode={false} />
            </span>
            <span style={styles.subtotalPrice}>
              <CurrencyFormatter value={item.subtotal} currencyCode={false} />
            </span>
          </div>
        </div>
      ))}
      <div style={styles.divider} />
      {/* Receipt discount */}
      {renderDiscountSection()}
      {/* Receipt subtotal */}
      <div style={styles.row}>
        <span>{t("total_without_tax")}</span>
        <span>
          <CurrencyFormatter value={netSubTotal} />
        </span>
      </div>
      {/* Receipt tax */}
      <div style={styles.row}>
        <span>{t("tax")}</span>
        <span>
          <CurrencyFormatter value={order.tax} />
        </span>
      </div>
      <div style={styles.divider} />
      {/* Receipt total */}
      <div style={styles.row}>
        <span>{isVatExempt ? t("total") : t("total_with_tax")}</span>
        <span>
          <CurrencyFormatter value={order.amount_charged} />
        </span>
      </div>
      <div style={styles.divider} />
      {/* qr code */}
      <div className="app-text-center">
        <br />
        <small>{t("printed_by_anypos")}</small>
        <br />
      </div>
    </div>
  );
});

export default PrintOrder;
